import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  previewRollForward, runRollForward, undoRollForwardBatch,
} from "../../api";

const CATEGORY_LABELS = {
  personal: "Personal",
  corporate: "Corporate",
  tslips: "T-Slips",
  other: "Other",
};

const th = {
  textAlign: "left", fontSize: 12, color: "#6b7280", fontWeight: 600,
  padding: "6px 8px", borderBottom: "1px solid #e5e7eb",
};
const td = { fontSize: 13, padding: "6px 8px", borderBottom: "1px solid #f3f4f6" };

const keyOf = (p) => `${p.kind}:${p.source_id}`;

// The source task's status as it stands right now, so a manager can see what
// they are rolling forward. The backend resolves the label (see
// _status_display in roll_forward.py); this only paints it.
const statusCell = (p) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
    <span
      aria-hidden="true"
      style={{
        width: 8, height: 8, borderRadius: "50%", flexShrink: 0,
        background: p.status_color || "#9ca3af",
      }}
    />
    {p.status_label || "Not set"}
  </span>
);

export default function RollForwardPreview({ scope, onDone, onLabel }) {
  const [plan, setPlan] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const [showIneligible, setShowIneligible] = useState(false);
  const [archiveSources, setArchiveSources] = useState(true);
  const [batch, setBatch] = useState(null);
  const [undone, setUndone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const scopeKey = JSON.stringify(scope);

  // Kept in a ref (rather than a `load` dependency) so a caller passing a
  // fresh `onLabel` closure on every render never invalidates `load`'s
  // memoization and re-triggers a fetch — only `scopeKey` should do that.
  const onLabelRef = useRef(onLabel);
  useEffect(() => {
    onLabelRef.current = onLabel;
  });

  const load = useCallback(() => {
    let live = true;
    // A new scope (or a retry) invalidates any prior run's result strip —
    // otherwise a stale batch (and its Undo button, closed over the OLD
    // batch id) would linger on screen after the picker moves on.
    setPlan(null);
    setBatch(null);
    setUndone(null);
    setErr("");
    setShowIneligible(false);
    previewRollForward(scope)
      .then((data) => {
        if (!live) return;
        setPlan(data);
        // Let the caller (e.g. a modal heading) know the scope label without
        // it having to fetch the preview a second time itself.
        onLabelRef.current?.(data.scope_label || "");
        // Policy pre-ticks; it never blocks. Ineligible rows stay available.
        setChecked(new Set(
          (data.proposals || []).filter((p) => p.eligible).map(keyOf),
        ));
      })
      .catch(() => live && setErr("Could not load the roll-forward preview."));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  useEffect(() => load(), [load]);

  const eligible = useMemo(
    () => (plan?.proposals || []).filter((p) => p.eligible), [plan],
  );
  const ineligible = useMemo(
    () => (plan?.proposals || []).filter((p) => !p.eligible), [plan],
  );

  const toggle = useCallback((proposal) => {
    setChecked((prev) => {
      const next = new Set(prev);
      const key = keyOf(proposal);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const selected = useMemo(
    () => (plan?.proposals || [])
      .filter((p) => checked.has(keyOf(p)))
      .map((p) => [p.kind, p.source_id]),
    [plan, checked],
  );

  const run = async () => {
    setBusy(true);
    setErr("");
    try {
      const result = await runRollForward({
        ...scope, selected, archive_sources: archiveSources,
      });
      setBatch(result);
      if (onDone) onDone(result);
    } catch (e) {
      setErr("The roll-forward could not be completed.");
    } finally {
      setBusy(false);
    }
  };

  const doUndo = async () => {
    setBusy(true);
    setErr("");
    try {
      setUndone(await undoRollForwardBatch(batch.id));
    } catch (e) {
      setErr("The undo could not be completed.");
    } finally {
      setBusy(false);
    }
  };

  if (err && !plan) {
    return (
      <div>
        <p style={{ color: "#b91c1c", fontSize: 13 }}>{err}</p>
        <button
          onClick={load}
          style={{ marginTop: 8, fontSize: 13, padding: "6px 12px", background: "white", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
        >
          Retry
        </button>
      </div>
    );
  }
  if (!plan) return <p style={{ fontSize: 13, color: "#6b7280" }}>Loading…</p>;

  if (batch) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8 }}>
        <span style={{ fontSize: 14, color: "#166534" }}>
          {batch.created_count} task{batch.created_count === 1 ? "" : "s"} created.
          {batch.skipped_count ? ` ${batch.skipped_count} skipped.` : ""}
        </span>
        {undone ? (
          <span style={{ fontSize: 13, color: "#6b7280" }}>
            Undone — {undone.deleted} removed
            {undone.kept ? `, ${undone.kept} kept because they had been edited` : ""}
            {undone.unarchived ? `, ${undone.unarchived} unarchived` : ""}.
          </span>
        ) : (
          <button
            onClick={doUndo}
            disabled={busy}
            style={{ fontSize: 13, padding: "6px 12px", background: "white", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}
          >
            Undo
          </button>
        )}
        {err && <span style={{ fontSize: 13, color: "#b91c1c" }}>{err}</span>}
      </div>
    );
  }

  if (!plan.proposals.length && !plan.skipped.length) {
    return <p style={{ fontSize: 14, color: "#6b7280" }}>Nothing to roll forward here.</p>;
  }

  const row = (p) => (
    <tr key={keyOf(p)}>
      <td style={td}>
        <input
          type="checkbox"
          aria-label={p.source_label}
          checked={checked.has(keyOf(p))}
          onChange={() => toggle(p)}
        />
      </td>
      <td style={td}>{p.client_name || p.source_label}</td>
      <td style={td}>{CATEGORY_LABELS[p.category] || p.category}</td>
      <td style={td}>{statusCell(p)}</td>
      <td style={td}>{p.new_year_end || "—"}</td>
      <td style={td}>{p.new_due_date || "—"}</td>
      <td style={{ ...td, color: "#9ca3af" }}>{p.reason}</td>
    </tr>
  );

  return (
    <div>
      {plan.proposals.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={th} />
              <th style={th}>Client</th>
              <th style={th}>Category</th>
              <th style={th}>Status</th>
              <th style={th}>New year end</th>
              <th style={th}>New due date</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {eligible.map(row)}
            {showIneligible && ineligible.map(row)}
          </tbody>
        </table>
      )}

      {ineligible.length > 0 && !showIneligible && (
        <button
          onClick={() => setShowIneligible(true)}
          style={{ marginTop: 8, fontSize: 13, background: "none", border: "none", color: "#3730a3", cursor: "pointer", fontWeight: 600, padding: 0 }}
        >
          Show {ineligible.length} more candidate{ineligible.length === 1 ? "" : "s"}
        </button>
      )}

      {plan.skipped.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <p style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, marginBottom: 4 }}>
            Skipped
          </p>
          {plan.skipped.map((s) => (
            <p key={`${s.kind}:${s.source_id}`} style={{ fontSize: 12, color: "#9ca3af", margin: "2px 0" }}>
              {s.source_label} — <span>{s.reason}</span>
            </p>
          ))}
        </div>
      )}

      <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 16, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={archiveSources}
          onChange={(e) => setArchiveSources(e.target.checked)}
        />
        Archive the source tasks I&rsquo;m rolling forward
        <span style={{ color: "#9ca3af" }}>(completed ones only)</span>
      </label>

      {err && <p style={{ color: "#b91c1c", fontSize: 13 }}>{err}</p>}

      <button
        onClick={run}
        disabled={busy || selected.length === 0}
        style={{ marginTop: 16, background: "#0F172A", color: "white", borderRadius: 8, padding: "10px 16px", border: "none", fontWeight: 600, fontSize: 14, cursor: selected.length ? "pointer" : "not-allowed", opacity: selected.length ? 1 : 0.5 }}
      >
        Roll forward {selected.length} task{selected.length === 1 ? "" : "s"}
      </button>
    </div>
  );
}
